import { useMutation, useQuery } from "@apollo/client";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { matchNameToObject } from "@src/utils/matchNameToObject";
import { gql } from "@src/__generated__";
import { HrLocationFieldsFragment } from "@src/__generated__/graphql";
import { useEffect, useState } from "react";

gql(/* GraphQL */ `
  fragment HrLocationFields on HrLocationObject {
    id
    name
    locationId
  }
`);

const GET_HR_LOCATIONS = gql(/* GraphQL */ `
  query GetHrLocations {
    hrLocations {
      ...HrLocationFields
    }
    locations {
      id
      name
    }
  }
`);

const UPDATE_HR_LOCATION_MAPPINGS = gql(/* GraphQL */ `
  mutation UpdateHrLocationMappings($hrLocations: [HrLocationInput!]!) {
    updateHrLocations(hrLocations: $hrLocations)
  }
`);

export type HrLocationMapping = HrLocationFieldsFragment & {
  isInitiallyUnmapped: boolean;
  newLocationName: string | null;
};

export const useHrLocationsManager = () => {
  const { data, loading, refetch } = useQuery(GET_HR_LOCATIONS);
  const [hrLocations, setHrLocations] = useState<HrLocationMapping[]>([]);
  const [saveHrLocations] = useMutation(UPDATE_HR_LOCATION_MAPPINGS);

  useEffect(() => {
    if (data) {
      setHrLocations(
        data.hrLocations.map((hrLocation) => {
          const locationId = matchNameToObject(hrLocation.name, data.locations);
          const newLocationName = locationId ? null : hrLocation.name;
          return {
            ...hrLocation,
            locationId,
            isInitiallyUnmapped: !hrLocation.locationId,
            newLocationName,
          };
        }),
      );
    }
  }, [data]);

  function updateHrLocation(
    id: string,
    newLocationId: string | null,
    newNewLocationName: string | null,
  ) {
    setHrLocations(
      hrLocations.map((hrLocation) =>
        hrLocation.id === id
          ? {
              ...hrLocation,
              locationId: newLocationId,
              newLocationName: newNewLocationName,
            }
          : hrLocation,
      ),
    );
  }

  function onNewLocationName(newLocationName: string, id: string) {
    updateHrLocation(id, null, newLocationName);
  }

  function onLocationChange(newLocationId: SelectOption | null, id: string) {
    if (newLocationId) {
      updateHrLocation(
        id,
        newLocationId.value || null,
        newLocationId.value ? null : newLocationId.label,
      );
    }
  }

  const locationOptions = (
    data?.locations.map(
      (location) =>
        ({
          value: location.id,
          label: location.name,
        } as SelectOption),
    ) ?? []
  ).concat(
    Array.from(
      new Set(
        hrLocations
          .map((hrLocation) => hrLocation.newLocationName)
          .filter((n) => !!n),
      ),
    ).map((name) => ({ label: name ?? "", value: "" })),
  );

  return {
    hrLocations,
    refetch,
    setHrLocations,
    saveHrLocations,
    updateHrLocation,
    onNewLocationName,
    onLocationChange,
    locations: data?.locations ?? [],
    locationOptions,
    loading,
  };
};
